import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Brain, Send, Loader, X, Minimize2, Maximize2, Trash2, Copy, RefreshCw, Sparkles, Mic, MicOff, Volume2, VolumeX, Clock, MessageSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useTheme } from '../theme/ThemeProvider';
import AIService from '../services/aiService';
import { generateCRMPrompts, organizePromptsByCategory, getPromptCategories } from '../utils/crmPromptGenerator';

interface AICopilotProps {
  context: any;
  isVisible: boolean;
  onToggle: () => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface Prompt {
  id: string;
  category: string;
  label: string;
  prompt: string;
  icon: string;
}

interface QueryHistoryItem {
  id: string;
  query: string;
  timestamp: string;
}

const AICopilot: React.FC<AICopilotProps> = ({ context, isVisible, onToggle }) => {
  const { theme } = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isGeneratingPrompts, setIsGeneratingPrompts] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Voice input state
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // Voice output (TTS) state
  const [speakingMessageIdx, setSpeakingMessageIdx] = useState<number | null>(null);
  const [ttsSupported, setTtsSupported] = useState(false);

  // Query history state
  const [queryHistory, setQueryHistory] = useState<QueryHistoryItem[]>([]);

  // Load query history from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('copilot_query_history');
    if (saved) {
      try {
        setQueryHistory(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse query history:', e);
      }
    }
  }, []);

  // Save query to history
  const saveQueryToHistory = (query: string) => {
    const newItem: QueryHistoryItem = {
      id: `q_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      query,
      timestamp: new Date().toISOString()
    };
    const updated = [newItem, ...queryHistory].slice(0, 100);
    setQueryHistory(updated);
    localStorage.setItem('copilot_query_history', JSON.stringify(updated));
  };

  // Clear all history
  const clearHistory = () => {
    setQueryHistory([]);
    localStorage.removeItem('copilot_query_history');
  };

  // Group history by date
  const groupedHistory = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: { label: string; items: QueryHistoryItem[] }[] = [
      { label: 'Today', items: [] },
      { label: 'Yesterday', items: [] },
      { label: 'Older', items: [] }
    ];

    queryHistory.forEach(item => {
      const d = new Date(item.timestamp);
      d.setHours(0, 0, 0, 0);
      if (d.getTime() === today.getTime()) groups[0].items.push(item);
      else if (d.getTime() === yesterday.getTime()) groups[1].items.push(item);
      else groups[2].items.push(item);
    });

    return groups.filter(g => g.items.length > 0);
  }, [queryHistory]);

  // Detect browser support for Speech Recognition and TTS
  useEffect(() => {
    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognitionAPI) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognitionRef.current = recognition;
    }
    if ('speechSynthesis' in window) {
      setTtsSupported(true);
    }
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (_e) { /* ignore */ }
      }
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Generate dynamic prompts when context changes
  useEffect(() => {
    const loadPrompts = async () => {
      if (!context) return;

      setIsGeneratingPrompts(true);
      try {
        // Generate CRM-specific prompts based on module/context
        const generatedPrompts = await generateCRMPrompts(context);
        setPrompts(generatedPrompts);
      } catch (error) {
        console.error('Error generating prompts:', error);
      } finally {
        setIsGeneratingPrompts(false);
      }
    };

    loadPrompts();
  }, [context]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Get categories from prompts
  const categories = ['All', ...getPromptCategories(prompts).map(c => c.name)];

  // Filter prompts by selected category
  const filteredPrompts = selectedCategory === 'All'
    ? prompts
    : prompts.filter(p => p.category === selectedCategory);

  // Handle sending a message — uses true SSE streaming for fast response
  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    // Save to history
    saveQueryToHistory(messageText);

    try {
      // Create AI message placeholder
      const aiMessage: Message = {
        role: 'assistant',
        content: '',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);

      // Create message with context
      const contextualMessage = AIService.createMessageWithContext(messageText, context);

      // Stream the response via SSE — text appears within ~500ms
      const response = await AIService.chatStream(
        [contextualMessage],
        (_chunk, fullResponse) => {
          // Hide loading spinner once first text arrives
          setIsLoading(false);
          setMessages(prev => {
            const newMessages = [...prev];
            if (newMessages.length > 0) {
              newMessages[newMessages.length - 1] = {
                ...aiMessage,
                content: fullResponse
              };
            }
            return newMessages;
          });
        },
        (actionProposal) => {
          // Handle tool use action proposals
          console.log('Action proposed:', actionProposal);
        }
      );

      // Ensure the final message is set
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages.length > 0 && newMessages[newMessages.length - 1].role === 'assistant') {
          newMessages[newMessages.length - 1] = {
            ...aiMessage,
            content: response
          };
        }
        return newMessages;
      });

    } catch (error: any) {
      const errorMsg = error.message || 'Failed to get AI response. Please try again.';
      setError(errorMsg);

      // Remove the empty AI message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle prompt click
  const handlePromptClick = (prompt: Prompt) => {
    handleSendMessage(prompt.prompt);
  };

  // Handle input submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage(inputValue);
  };

  // Clear conversation
  const handleClear = async () => {
    setMessages([]);
    setError(null);
    // Clear backend conversation history
    await AIService.clearConversation();
  };

  // Copy message to clipboard
  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    // Could add a toast notification here
  };

  // --- Voice Input Functions ---
  const startListening = () => {
    const recognition = recognitionRef.current;
    if (!recognition || isListening) return;
    setIsListening(true);
    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results)
        .map((result: any) => result[0].transcript)
        .join('');
      setInputValue(transcript);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone permission in your browser settings.');
      } else if (event.error === 'network') {
        alert('Network error during speech recognition. Please check your connection.');
      }
    };
    try { recognition.start(); } catch (_e) { setIsListening(false); }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const toggleListening = () => {
    if (isListening) stopListening();
    else startListening();
  };

  // --- Voice Output (TTS) Functions ---
  const speakMessage = (text: string, messageIdx: number) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const cleanText = text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/#{1,6}\s/g, '')
      .replace(/[-*]\s/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\n{2,}/g, '. ')
      .replace(/\n/g, '. ')
      .trim();
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.lang = 'en-US';
    utterance.onstart = () => setSpeakingMessageIdx(messageIdx);
    utterance.onend = () => setSpeakingMessageIdx(null);
    utterance.onerror = () => setSpeakingMessageIdx(null);
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setSpeakingMessageIdx(null);
    }
  };

  if (!isVisible) return null;

  // Format time for history items
  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      className="flex flex-row h-full"
      style={{
        backgroundColor: theme.background,
        borderLeft: `1px solid ${theme.border}`
      }}
    >
      {/* ═══ LEFT: Query History Sidebar — 50% ═══ */}
      <div
        className="w-1/2 flex flex-col h-full"
        style={{ borderRight: `1px solid ${theme.border}` }}
      >
        {/* History Header */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            background: '#0d0e1a',
            borderBottom: `1px solid rgba(255,255,255,0.08)`
          }}
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" style={{ color: '#00BBA2' }} />
            <h3 className="font-semibold text-sm" style={{ color: 'rgba(255,255,255,0.88)' }}>
              Query History
            </h3>
          </div>
          {queryHistory.length > 0 && (
            <button
              onClick={clearHistory}
              className="p-1.5 rounded hover:opacity-80 flex items-center gap-1"
              title="Clear all history"
              style={{ color: '#D4D4D4' }}
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span className="text-xs">Clear</span>
            </button>
          )}
        </div>

        {/* History List */}
        <div className="flex-1 overflow-y-auto">
          {queryHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-4">
              <MessageSquare className="w-8 h-8 mb-2" style={{ color: theme.border }} />
              <p className="text-xs text-center" style={{ color: theme.textSecondary }}>
                No queries yet. Ask a question to get started.
              </p>
            </div>
          ) : (
            <div className="py-2">
              {groupedHistory.map(group => (
                <div key={group.label}>
                  <div
                    className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider"
                    style={{ color: theme.textSecondary }}
                  >
                    {group.label}
                  </div>
                  {group.items.map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleSendMessage(item.query)}
                      className="w-full text-left px-4 py-2.5 transition-colors"
                      style={{ color: theme.text }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.surfaceAlt}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div className="text-xs truncate" title={item.query}>
                        {item.query.length > 60 ? item.query.substring(0, 60) + '...' : item.query}
                      </div>
                      <div className="text-[10px] mt-0.5" style={{ color: theme.textSecondary }}>
                        {formatTime(item.timestamp)}
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ═══ RIGHT: Chat Area — 50% ═══ */}
      <div className="w-1/2 flex flex-col h-full">
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            background: '#0d0e1a',
            borderBottom: `1px solid rgba(255,255,255,0.08)`
          }}
        >
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5" style={{ color: '#00BBA2' }} />
            <h3 className="font-semibold text-sm" style={{ color: 'rgba(255,255,255,0.88)' }}>
              Sales Copilot
            </h3>
            <Sparkles className="w-4 h-4" style={{ color: '#00BBA2' }} />
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={handleClear}
                className="p-1.5 rounded hover:opacity-80"
                title="Clear conversation"
                style={{ color: '#D4D4D4' }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onToggle}
              className="p-1.5 rounded hover:opacity-80"
              title="Close copilot"
              style={{ color: '#D4D4D4' }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Context Info */}
        {context && (
          <div
            className="px-4 py-2 text-xs"
            style={{
              backgroundColor: theme.mode === 'light' ? 'rgba(0,187,162,0.06)' : 'rgba(0,187,162,0.1)',
              borderBottom: `1px solid ${theme.border}`,
              color: theme.textSecondary
            }}
          >
            <div className="flex items-center justify-between">
              <span>
                {context.module || context.view || 'Dashboard'} {context.asOfDate && `• ${context.asOfDate}`}
              </span>
              <span>
                {context.summary?.total || context.summary?.totalRecords || 0} records
              </span>
            </div>
          </div>
        )}

        {/* Quick Prompts Section */}
        {messages.length === 0 && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="mb-4">
              <h4 className="text-xs font-semibold mb-2" style={{ color: theme.text }}>
                Sales Copilot
              </h4>
              <p className="text-xs mb-3" style={{ color: theme.textSecondary }}>
                Ask about your pipeline, deals needing action, or team workload:
              </p>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-1 mb-3">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className="px-2 py-1 text-xs rounded transition-all"
                    style={{
                      backgroundColor: selectedCategory === category ? theme.primary : theme.surface,
                      color: selectedCategory === category ? '#ffffff' : theme.text,
                      border: `1px solid ${selectedCategory === category ? theme.primary : theme.border}`
                    }}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Prompt Buttons */}
              {isGeneratingPrompts ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin" style={{ color: theme.primary }} />
                  <span className="ml-2 text-sm" style={{ color: theme.textSecondary }}>
                    Generating insights...
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredPrompts.map(prompt => (
                    <button
                      key={prompt.id}
                      onClick={() => handlePromptClick(prompt)}
                      disabled={isLoading}
                      className="w-full text-left px-3 py-2 rounded text-xs transition-all"
                      style={{
                        backgroundColor: theme.surface,
                        border: `1px solid ${theme.border}`,
                        color: theme.text
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.surfaceAlt}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.surface}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-base">{prompt.icon}</span>
                        <div className="flex-1">
                          <div className="font-semibold">{prompt.label}</div>
                          <div className="text-[10px] opacity-70 mt-0.5">
                            {prompt.prompt.substring(0, 80)}...
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Messages Area */}
        {messages.length > 0 && (
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
                    message.role === 'user' ? 'rounded-br-none' : 'rounded-bl-none'
                  }`}
                  style={{
                    backgroundColor: message.role === 'user'
                      ? theme.primary
                      : theme.surface,
                    color: message.role === 'user' ? '#ffffff' : theme.text,
                    border: message.role === 'assistant' ? `1px solid ${theme.border}` : 'none'
                  }}
                >
                  {message.role === 'user' ? (
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  ) : (
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown
                        components={{
                        h1: ({children, ...props}) => <h1 className="text-lg font-bold mt-3 mb-2" style={{color: theme.text}} {...props}>{children}</h1>,
                        h2: ({children, ...props}) => <h2 className="text-base font-bold mt-3 mb-2" style={{color: theme.text}} {...props}>{children}</h2>,
                        h3: ({children, ...props}) => <h3 className="text-sm font-bold mt-2 mb-1" style={{color: theme.text}} {...props}>{children}</h3>,
                        p: ({children, ...props}) => <p className="mb-2" style={{color: theme.text}} {...props}>{children}</p>,
                        ul: ({children, ...props}) => <ul className="list-disc list-inside mb-2 space-y-1" style={{color: theme.text}} {...props}>{children}</ul>,
                        ol: ({children, ...props}) => <ol className="list-decimal list-inside mb-2 space-y-1" style={{color: theme.text}} {...props}>{children}</ol>,
                        li: ({children, ...props}) => <li className="ml-2" style={{color: theme.text}} {...props}>{children}</li>,
                        strong: ({children, ...props}) => <strong className="font-bold" style={{color: theme.text}} {...props}>{children}</strong>,
                        em: ({children, ...props}) => <em className="italic" style={{color: theme.text}} {...props}>{children}</em>,
                        code: ({inline, children, ...props}: any) =>
                          inline ? (
                            <code className="px-1 py-0.5 rounded text-xs font-mono" style={{backgroundColor: theme.surfaceAlt, color: theme.text}} {...props}>{children}</code>
                          ) : (
                            <code className="block px-2 py-1 rounded text-xs font-mono my-2" style={{backgroundColor: theme.surfaceAlt, color: theme.text}} {...props}>{children}</code>
                          ),
                        pre: ({children, ...props}) => <pre className="mb-2 overflow-x-auto" {...props}>{children}</pre>,
                        blockquote: ({children, ...props}) => <blockquote className="border-l-2 pl-3 my-2 italic" style={{borderColor: theme.border, color: theme.textSecondary}} {...props}>{children}</blockquote>,
                        a: ({children, ...props}) => <a className="underline hover:opacity-80" style={{color: theme.primary}} {...props}>{children}</a>,
                        hr: (props) => <hr className="my-3" style={{borderColor: theme.border}} {...props} />,
                        table: ({children, ...props}) => <table className="border-collapse w-full my-2 text-xs" style={{borderColor: theme.border}} {...props}>{children}</table>,
                        thead: ({children, ...props}) => <thead style={{backgroundColor: theme.surfaceAlt}} {...props}>{children}</thead>,
                        tbody: ({children, ...props}) => <tbody {...props}>{children}</tbody>,
                        tr: ({children, ...props}) => <tr className="border-b" style={{borderColor: theme.border}} {...props}>{children}</tr>,
                        th: ({children, ...props}) => <th className="border px-2 py-1 text-left font-semibold" style={{borderColor: theme.border, color: theme.text}} {...props}>{children}</th>,
                        td: ({children, ...props}) => <td className="border px-2 py-1" style={{borderColor: theme.border, color: theme.text}} {...props}>{children}</td>
                      }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  )}
                  {message.role === 'assistant' && message.content && (
                    <div className="flex items-center gap-2 mt-2 pt-2"
                      style={{ borderTop: `1px solid ${theme.borderLight}` }}>
                      <button
                        onClick={() => handleCopy(message.content)}
                        className="text-xs opacity-70 hover:opacity-100 flex items-center gap-1"
                        style={{ color: theme.textSecondary }}
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </button>
                      {ttsSupported && (
                        <button
                          onClick={() => speakingMessageIdx === index ? stopSpeaking() : speakMessage(message.content, index)}
                          className="text-xs opacity-70 hover:opacity-100 flex items-center gap-1"
                          style={{ color: speakingMessageIdx === index ? theme.primary : theme.textSecondary }}
                          title={speakingMessageIdx === index ? 'Stop reading' : 'Read aloud'}
                        >
                          {speakingMessageIdx === index ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                          {speakingMessageIdx === index ? 'Stop' : 'Listen'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div
                  className="rounded-lg rounded-bl-none px-3 py-2 flex items-center gap-2"
                  style={{
                    backgroundColor: theme.surface,
                    border: `1px solid ${theme.border}`
                  }}
                >
                  <Loader className="w-4 h-4 animate-spin" style={{ color: theme.primary }} />
                  <span className="text-sm" style={{ color: theme.textSecondary }}>
                    Analyzing...
                  </span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div
            className="mx-4 mb-2 px-3 py-2 rounded text-xs"
            style={{
              backgroundColor: theme.mode === 'light' ? '#FEE2E2' : '#7F1D1D20',
              color: theme.error,
              border: `1px solid ${theme.error}`
            }}
          >
            {error}
          </div>
        )}

        {/* Input Area */}
        <div
          className="p-4"
          style={{
            borderTop: `1px solid ${theme.border}`,
            backgroundColor: theme.surfaceAlt
          }}
        >
          {isListening && (
            <div className="flex items-center gap-2 px-3 py-1.5 mb-2 rounded"
              style={{ backgroundColor: 'rgba(0,187,162,0.08)', border: `1px solid rgba(0,187,162,0.3)` }}>
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: theme.primary }} />
              <span className="text-xs font-medium" style={{ color: theme.primary }}>Listening... speak now</span>
              <button onClick={stopListening} className="ml-auto text-xs hover:opacity-80" style={{ color: theme.textSecondary }}>Cancel</button>
            </div>
          )}
          <form onSubmit={handleSubmit} className="flex gap-2 items-center">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={isListening ? "Listening..." : "Ask about leads, deals, contacts..."}
              disabled={isLoading}
              className="flex-1 px-3 py-2 rounded text-sm"
              style={{
                backgroundColor: theme.background,
                border: `1px solid ${isListening ? theme.primary : theme.border}`,
                color: theme.text
              }}
            />
            {speechSupported && (
              <button
                type="button"
                onClick={toggleListening}
                disabled={isLoading}
                className="px-3 py-2 rounded disabled:opacity-50 transition-all"
                style={{
                  backgroundColor: isListening ? theme.primary : theme.surface,
                  color: isListening ? '#ffffff' : theme.textSecondary,
                  border: `1px solid ${isListening ? theme.primary : theme.border}`,
                  animation: isListening ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
                }}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              className="px-3 py-2 rounded disabled:opacity-50"
              style={{
                backgroundColor: theme.primary,
                color: '#ffffff'
              }}
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <div className="mt-2 text-[10px] text-center" style={{ color: theme.textSecondary }}>
            Powered by Claude AI • Context-aware CRM insights
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICopilot;
